import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DashboardLayout from "@/components/DashboardLayout";
import { Search, Eye, EyeOff, LogIn, Copy, RefreshCw, KeyRound, Ban, ShieldCheck, Zap, ZapOff, Pencil, Coins } from "lucide-react";
import StatusPill from "@/components/StatusPill";
import { toast } from "sonner";
import EditMemberModal from "@/components/admin/EditMemberModal";
import SpAdjustModal from "@/components/admin/SpAdjustModal";

const ROLES = [
  { k: "", l: "All" },
  { k: "customer", l: "Customer" },
  { k: "distributor", l: "IBA" },
  { k: "franchise", l: "Franchise" },
  { k: "admin", l: "Admin" },
];

export default function AdminMembers() {
  const { startImpersonation, user: me } = useAuth();
  const isSuperadmin = me?.role === "superadmin";
  const isAdmin = me?.role === "admin" || isSuperadmin;
  const [rows, setRows] = useState([]);
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [busyCode, setBusyCode] = useState(null);
  const [resettingCode, setResettingCode] = useState(null);
  const [activatingCode, setActivatingCode] = useState(null);
  const [revealed, setRevealed] = useState({}); // user_code -> bool
  const [editMember, setEditMember] = useState(null);
  const [spMember, setSpMember] = useState(null);

  const load = () => {
    const params = new URLSearchParams();
    if (role) params.append("role", role);
    if (q) params.append("q", q);
    api.get(`/admin/members?${params}`).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [role, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const loginAs = async (u) => {
    if (u.role === "admin") { toast.error("Cannot impersonate another admin"); return; }
    if (!window.confirm(`Login as ${u.name} (${u.user_code})?\nThis is audit-logged. Session expires in 60 minutes.`)) return;
    setBusyCode(u.user_code);
    try {
      const { data } = await api.post(`/admin/users/${u.user_code}/impersonate`);
      startImpersonation({ token: data.token, user: data.user });
      toast.success(`Now viewing as ${data.user.name}`);
      window.location.assign("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impersonation failed");
    } finally {
      setBusyCode(null);
    }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.name} (${u.user_code})?\nThe old password will stop working immediately. The new one appears in this row and is copied to your clipboard.`)) return;
    setResettingCode(u.user_code);
    try {
      const { data } = await api.post(`/admin/users/${u.user_code}/reset-password`);
      setRows((prev) => prev.map((r) => r.user_code === u.user_code
        ? { ...r, password_plain: data.password_plain, password_updated_at: data.password_updated_at }
        : r));
      setRevealed((prev) => ({ ...prev, [u.user_code]: true }));
      try {
        await navigator.clipboard.writeText(data.password_plain);
        toast.success(`New password for ${u.user_code} copied to clipboard`);
      } catch {
        toast.success(`Password reset for ${u.user_code}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResettingCode(null);
    }
  };

  const copyPw = (u) => {
    if (!u.password_plain) return;
    navigator.clipboard.writeText(u.password_plain);
    toast.success("Password copied");
  };

  const toggleBlock = async (u) => {
    if (u.role === "admin") { toast.error("Cannot block another admin"); return; }
    const willBlock = !u.is_blocked;
    const verb = willBlock ? "block" : "unblock";
    if (!window.confirm(`${verb.toUpperCase()} ${u.name} (${u.user_code})?\n${willBlock ? "They will not be able to log in until unblocked." : "They will be able to log in again."}\nThis action is audit-logged.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.user_code}/toggle-block`);
      setRows((prev) => prev.map((r) => r.user_code === u.user_code ? { ...r, is_blocked: data.is_blocked } : r));
      toast.success(`${u.user_code} ${data.is_blocked ? "blocked" : "unblocked"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggleReveal = (code) => setRevealed((p) => ({ ...p, [code]: !p[code] }));

  // Superadmin-only: activate a member without KYC or package purchase.
  // Confirms twice because it's a compliance-sensitive override.
  const forceActivate = async (u) => {
    if (!isSuperadmin) return;
    if (u.active) { toast.info(`${u.user_code} is already active`); return; }
    if (!window.confirm(
      `Force-activate ${u.name} (${u.user_code}) WITHOUT KYC documents or a package purchase?\n\n` +
      `This is a superadmin override — audit-logged. Use only for verified partners, ` +
      `in-house testers or offline enrollments.\n\nContinue?`
    )) return;
    setActivatingCode(u.user_code);
    try {
      await api.post(`/superadmin/users/${u.user_code}/force-activate`);
      setRows((prev) => prev.map((r) => r.user_code === u.user_code ? { ...r, active: true } : r));
      toast.success(`${u.user_code} activated (superadmin override)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Force-activate failed");
    } finally {
      setActivatingCode(null);
    }
  };

  // Superadmin-only: reverse of forceActivate. Wallets/SP/downline preserved.
  const deactivate = async (u) => {
    if (!isSuperadmin) return;
    if (!u.active) { toast.info(`${u.user_code} is already inactive`); return; }
    const reason = window.prompt(
      `Deactivate ${u.name} (${u.user_code})?\n\nOptional reason (audit-logged):`,
      ""
    );
    if (reason === null) return; // user hit Cancel
    setActivatingCode(u.user_code);
    try {
      await api.post(`/superadmin/users/${u.user_code}/deactivate`, { reason });
      setRows((prev) => prev.map((r) => r.user_code === u.user_code ? { ...r, active: false } : r));
      toast.success(`${u.user_code} deactivated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deactivate failed");
    } finally {
      setActivatingCode(null);
    }
  };

  return (
    <DashboardLayout title="Members" subtitle={`${rows.length} member${rows.length !== 1 ? "s" : ""}`}>
      <div className="bg-white border border-border p-4 rounded-sm mb-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
          <input data-testid="member-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, code…" className="w-full border border-border pl-9 pr-3 py-2 text-sm rounded-sm focus:border-gold focus:outline-none" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {ROLES.map((r) => (
            <button key={r.k} data-testid={`role-${r.k || "all"}`} onClick={() => setRole(r.k)} className={`px-3 py-1.5 text-xs border rounded-sm ${role === r.k ? "bg-navy text-gold border-navy" : "border-border text-navy hover:border-gold"}`}>{r.l}</button>
          ))}
        </div>
      </div>
      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="bg-navy text-white">
            <tr>
              {["Code", "Name", "Email", "Role", "Password", "DOJ", "Self SP", "Total SP", "KYC", ""].map((h) => <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-widest font-medium">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.user_code} data-testid={`member-${u.user_code}`} className="border-b border-border last:border-0 hover:bg-cream/40">
                <td className="px-4 py-3 font-medium text-navy">{u.user_code}</td>
                <td className="px-4 py-3 text-navy/70">{u.name}</td>
                <td className="px-4 py-3 text-navy/60">{u.email}</td>
                <td className="px-4 py-3 capitalize text-navy/60">
                  <div>{u.role === "distributor" ? "IBA" : u.role}</div>
                  {u.is_blocked && (
                    <span data-testid={`blocked-${u.user_code}`} className="mt-0.5 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-rose/10 text-rose">
                      <Ban size={9} /> Blocked
                    </span>
                  )}
                </td>
                <td className="px-4 py-3" data-testid={`pw-cell-${u.user_code}`}>
                  {u.password_plain ? (
                    <div className="inline-flex items-center gap-1.5">
                      <span
                        className="font-mono text-xs text-navy select-all"
                        data-testid={`pw-value-${u.user_code}`}
                      >
                        {revealed[u.user_code] ? u.password_plain : "•".repeat(Math.min(u.password_plain.length, 10))}
                      </span>
                      <button
                        type="button"
                        onClick={() => toggleReveal(u.user_code)}
                        title={revealed[u.user_code] ? "Hide" : "Reveal"}
                        data-testid={`pw-toggle-${u.user_code}`}
                        className="p-1 text-navy/50 hover:text-royal"
                      >
                        {revealed[u.user_code] ? <EyeOff size={11} /> : <Eye size={11} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => copyPw(u)}
                        title="Copy password"
                        data-testid={`pw-copy-${u.user_code}`}
                        className="p-1 text-navy/50 hover:text-royal"
                      >
                        <Copy size={11} />
                      </button>
                    </div>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest text-navy/40 italic">Legacy · reset</span>
                  )}
                </td>
                <td className="px-4 py-3 text-navy/50 text-xs">{formatDate(u.created_at)}</td>
                <td className="px-4 py-3">
                  <span className="text-olive font-semibold tabular-nums">{u.self_sp || u.package_sp || 0}</span>
                  {u.active ? (
                    <span className="ml-1.5 text-[9px] uppercase tracking-widest bg-olive/10 text-olive px-1 py-0.5 rounded">Active</span>
                  ) : (
                    <span className="ml-1.5 text-[9px] uppercase tracking-widest bg-navy/10 text-navy/50 px-1 py-0.5 rounded">Inactive</span>
                  )}
                </td>
                <td className="px-4 py-3 text-olive tabular-nums">{u.total_sp || 0}</td>
                <td className="px-4 py-3">
                  <StatusPill status={u.kyc_status || "not_submitted"} size="sm" />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5 justify-end">
                    <button
                      onClick={() => resetPassword(u)}
                      disabled={resettingCode === u.user_code}
                      data-testid={`reset-pw-${u.user_code}`}
                      className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2 py-1 border border-royal/30 text-royal hover:bg-royal/5 rounded-sm disabled:opacity-50"
                      title="Reset & regenerate password (audit-logged)"
                    >
                      {resettingCode === u.user_code ? (
                        <RefreshCw size={11} className="animate-spin" />
                      ) : (
                        <KeyRound size={11} />
                      )}
                      Reset
                    </button>
                    <Link
                      to={`/admin/members/${u.user_code}`}
                      data-testid={`view-member-${u.user_code}`}
                      className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2 py-1 border border-gold/30 text-gold-deep hover:bg-gold/10 rounded-sm"
                    >
                      <Eye size={11} /> View
                    </Link>
                    {isAdmin && u.role !== "admin" && u.role !== "superadmin" && (
                      <button
                        onClick={() => setEditMember(u)}
                        data-testid={`edit-member-${u.user_code}`}
                        title="Edit member's basic profile (audit-logged)"
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2 py-1 rounded-sm border border-navy/30 text-navy hover:bg-navy/5"
                      >
                        <Pencil size={11} /> Edit
                      </button>
                    )}
                    {isAdmin && u.role !== "admin" && u.role !== "superadmin" && (
                      <button
                        onClick={() => setSpMember(u)}
                        data-testid={`sp-adjust-${u.user_code}`}
                        title="Adjust SP (admin + super-admin, audit-logged)"
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2 py-1 rounded-sm border border-olive/40 text-olive hover:bg-olive/10"
                      >
                        <Coins size={11} /> SP
                      </button>
                    )}
                    {u.role !== "admin" && (
                      <button
                        onClick={() => loginAs(u)}
                        disabled={busyCode === u.user_code}
                        data-testid={`login-as-${u.user_code}`}
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2 py-1 bg-navy text-gold border border-gold/30 hover:bg-navy-deep rounded-sm disabled:opacity-50"
                        title="Login as this user (audit-logged, 60 min session)"
                      >
                        <LogIn size={11} /> Login
                      </button>
                    )}
                    {u.role !== "admin" && (
                      <button
                        onClick={() => toggleBlock(u)}
                        data-testid={`block-toggle-${u.user_code}`}
                        title={u.is_blocked ? "Unblock this member" : "Block this member from logging in"}
                        className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2 py-1 rounded-sm border ${
                          u.is_blocked
                            ? "border-olive/40 text-olive hover:bg-olive/10"
                            : "border-rose/40 text-rose hover:bg-rose/10"
                        }`}
                      >
                        {u.is_blocked ? <><ShieldCheck size={11} /> Unblock</> : <><Ban size={11} /> Block</>}
                      </button>
                    )}
                    {isSuperadmin && u.role !== "admin" && u.role !== "superadmin" && !u.active && (
                      <button
                        onClick={() => forceActivate(u)}
                        disabled={activatingCode === u.user_code}
                        data-testid={`force-activate-${u.user_code}`}
                        title="Superadmin override — activate without KYC docs / package purchase (audit-logged)"
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2 py-1 rounded-sm border border-gold text-gold-deep bg-gold/5 hover:bg-gold/15 disabled:opacity-50"
                      >
                        {activatingCode === u.user_code ? (
                          <RefreshCw size={11} className="animate-spin" />
                        ) : (
                          <Zap size={11} />
                        )}
                        Activate
                      </button>
                    )}
                    {isSuperadmin && u.role !== "admin" && u.role !== "superadmin" && u.active && (
                      <button
                        onClick={() => deactivate(u)}
                        disabled={activatingCode === u.user_code}
                        data-testid={`deactivate-${u.user_code}`}
                        title="Superadmin override — flip active=false (SP, downline, wallets preserved · audit-logged)"
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2 py-1 rounded-sm border border-rose/50 text-rose bg-rose/5 hover:bg-rose/15 disabled:opacity-50"
                      >
                        {activatingCode === u.user_code ? (
                          <RefreshCw size={11} className="animate-spin" />
                        ) : (
                          <ZapOff size={11} />
                        )}
                        Deactivate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editMember && (
        <EditMemberModal
          member={editMember}
          onClose={() => setEditMember(null)}
          onSaved={() => load()}
        />
      )}
      {spMember && (
        <SpAdjustModal
          member={spMember}
          onClose={() => setSpMember(null)}
          onSaved={() => load()}
        />
      )}
    </DashboardLayout>
  );
}
